import { useEffect, useMemo, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
import { useNavigate } from "react-router";
import type { DashboardAuditCard } from "../types";

interface Props {
  audits: DashboardAuditCard[];
  /** CSS height for the map container. Defaults to 300px. */
  height?: number | string;
}

/**
 * Mini-map for the "today ± 1 day" panel on the dashboard. Renders
 * the audits that carry geometry as default Leaflet markers; click
 * → audit detail.
 */
export const TodayMap = ({ audits, height = 300 }: Props) => {
  const navigate = useNavigate();
  // Skip audits without geometry — they show up in the list but
  // can't be placed on the map.
  const located = useMemo(
    () => audits.filter((a) => a.lat != null && a.lng != null),
    [audits],
  );
  const mapRef = useRef<L.Map | null>(null);

  // Auto-fit on mount + on dataset change so the map always frames
  // the visible markers, regardless of how few or how scattered.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || located.length === 0) return;
    const bounds = L.latLngBounds(
      located.map((a) => [a.lat as number, a.lng as number]),
    );
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 13 });
    }
  }, [located]);

  if (located.length === 0) {
    return (
      <div
        style={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#8c8c8c",
          fontSize: 13,
          background: "#fafafa",
          border: "1px dashed #d9d9d9",
          borderRadius: 4,
        }}
      >
        Ninguna auditoría con geometría en la ventana actual
      </div>
    );
  }

  return (
    <MapContainer
      style={{ height, width: "100%", borderRadius: 4 }}
      center={[located[0].lat as number, located[0].lng as number]}
      zoom={12}
      whenReady={() => {
        // `whenReady` doesn't get the map instance in newer
        // versions; capture via the ref pattern below.
      }}
      ref={(m) => {
        mapRef.current = m;
      }}
      scrollWheelZoom={false}
    >
      <TileLayer
        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
        attribution="&copy; Esri"
        maxZoom={19}
      />
      {located.map((a) => (
        <Marker
          key={a.id}
          position={[a.lat as number, a.lng as number]}
          eventHandlers={{
            click: () => navigate(`/audits/${a.id}`),
          }}
        >
          <Popup>
            <div style={{ fontSize: 13 }}>
              <strong>{a.reference}</strong>
              <br />
              {a.farm_name || "—"}
              {a.producer_name ? ` · ${a.producer_name}` : ""}
            </div>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
};

export default TodayMap;
